import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { Send, MessageCircle, User, Truck, ShieldCheck } from "lucide-react";

function Message({ msg, adminId }) {
  const isAdmin = msg.sender_role === "admin";
  return (
    <div className={`flex ${isAdmin ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
        isAdmin
          ? "bg-slate-900 text-white rounded-br-sm"
          : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"
      }`}>
        {!isAdmin && (
          <div className="flex items-center gap-1.5 mb-1">
            {msg.sender_role === "pro"
              ? <Truck className="w-3 h-3 text-orange-500" />
              : <User className="w-3 h-3 text-blue-500" />
            }
            <span className={`text-[10px] font-bold uppercase tracking-wide ${
              msg.sender_role === "pro" ? "text-orange-500" : "text-blue-500"
            }`}>
              {msg.sender_name || "Utilisateur"} · {msg.sender_role}
            </span>
          </div>
        )}
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.body}</p>
        <div className={`text-[10px] mt-1 ${isAdmin ? "text-slate-400" : "text-slate-400"}`}>
          {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

export default function AdminSupport() {
  const { user } = useAuth();
  const [convs,       setConvs]       = useState([]);
  const [activeConv,  setActiveConv]  = useState(null);
  const [messages,    setMessages]    = useState([]);
  const [input,       setInput]       = useState("");
  const [sending,     setSending]     = useState(false);
  const bottomRef = useRef(null);
  const pollRef   = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/support/conversations");
      setConvs(data);
    } catch {}
  }, []);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      // Marque aussi la conversation comme lue côté admin
      const { data } = await api.get(`/support/messages/${convId}`);
      setMessages(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadConvs();
    pollRef.current = setInterval(() => {
      loadConvs();
      if (activeConv) loadMessages(activeConv);
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [loadConvs, loadMessages, activeConv]);

  useEffect(() => {
    if (activeConv) loadMessages(activeConv);
  }, [activeConv, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !activeConv) return;
    setSending(true);
    try {
      await api.post(`/support/messages/${activeConv}`, { body: input.trim() });
      setInput("");
      await loadMessages(activeConv);
      await loadConvs();
    } catch (err) {
      console.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const roleIcon = (role) => {
    if (role === "pro") return <Truck className="w-4 h-4 text-orange-500" />;
    return <User className="w-4 h-4 text-blue-500" />;
  };

  const roleColor = (role) => role === "pro" ? "text-orange-600 bg-orange-50" : "text-blue-600 bg-blue-50";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 flex" style={{ height: "calc(100vh - 4rem)" }}>

          {/* Liste des conversations */}
          <div className="w-80 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
            <div className="px-4 py-4 border-b border-slate-200">
              <div className="font-display font-bold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-orange-500" />
                Support admin
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{convs.length} conversation{convs.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {convs.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">Aucune conversation</div>
                </div>
              ) : convs.map(c => (
                <button
                  key={c.conversation_id}
                  onClick={() => setActiveConv(c.conversation_id)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    activeConv === c.conversation_id ? "bg-orange-50 border-l-2 border-l-orange-500" : ""
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {roleIcon(c.user_role)}
                      <span className="font-semibold text-sm text-slate-900 truncate">{c.user_name}</span>
                    </div>
                    {c.unread > 0 && (
                      <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {c.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{c.last_message}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${roleColor(c.user_role)}`}>
                      {c.user_role}
                    </span>
                    <span className="text-[10px] text-slate-400">{c.user_email}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Zone de chat */}
          <div className="flex-1 flex flex-col">
            {activeConv ? (
              <>
                {/* Header conv active */}
                <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3 flex-shrink-0">
                  {(() => {
                    const c = convs.find(x => x.conversation_id === activeConv);
                    return c ? (
                      <>
                        {roleIcon(c.user_role)}
                        <div>
                          <div className="font-display font-bold text-slate-900">{c.user_name}</div>
                          <div className="text-xs text-slate-500">{c.user_email} · {c.user_role}</div>
                        </div>
                      </>
                    ) : null;
                  })()}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {messages.map(msg => (
                    <Message key={msg.msg_id} msg={msg} adminId={user?.user_id} />
                  ))}
                  <div ref={bottomRef} />
                </div>

                {/* Input */}
                <form onSubmit={send} className="bg-white border-t border-slate-200 px-4 py-3 flex gap-3 flex-shrink-0">
                  <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="Répondre…"
                    className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-400"
                  />
                  <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="w-10 h-10 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                  >
                    {sending
                      ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <Send className="w-4 h-4" />
                    }
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <div className="font-display font-bold text-xl text-slate-900">Sélectionnez une conversation</div>
                  <p className="text-sm text-slate-500 mt-2">Cliquez sur une conversation pour répondre</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
